import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { formatApiError, useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Wrench, Check, Clock, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABELS = {
  pending: { label: "En attente de validation", icon: Clock, class: "bg-amber-100 text-amber-800 border-amber-200" },
  approved: { label: "Publié dans l'annuaire", icon: Check, class: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  rejected: { label: "Refusé", icon: AlertCircle, class: "bg-red-100 text-red-800 border-red-200" },
};

export default function TechnicianRegister() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    address: "",
    postal_code: "",
    city: "",
    brands_supported: [],
    certifications: "",
    description: "",
    website: "",
    service_radius_km: 30,
  });

  useEffect(() => {
    Promise.all([
      api.get("/technicians/meta/brands").then(({ data }) => setBrands(data.brands || [])),
      api.get("/technicians/me/profile").then(({ data }) => {
        if (data.technician) {
          const t = data.technician;
          setCurrent(t);
          setForm({
            company_name: t.company_name || "",
            contact_name: t.contact_name || "",
            email: t.email || user?.email || "",
            phone: t.phone || "",
            address: t.address || "",
            postal_code: t.postal_code || "",
            city: t.city || "",
            brands_supported: t.brands_supported || [],
            certifications: t.certifications || "",
            description: t.description || "",
            website: t.website || "",
            service_radius_km: t.service_radius_km || 30,
          });
        } else {
          setForm((f) => ({
            ...f,
            company_name: user?.company_name || "",
            contact_name: user?.name || "",
            email: user?.email || "",
          }));
        }
      }),
    ]).finally(() => setLoading(false));
  }, [user]);

  const toggleBrand = (v) => {
    setForm((f) => ({
      ...f,
      brands_supported: f.brands_supported.includes(v)
        ? f.brands_supported.filter((b) => b !== v)
        : [...f.brands_supported, v],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/technicians", form);
      setCurrent(data);
      toast.success(current ? "Fiche mise à jour — validation en cours" : "Fiche créée — validation en cours");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] grid place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const StatusInfo = current ? STATUS_LABELS[current.status] : null;

  return (
    <div className="mx-auto max-w-3xl px-6 lg:px-10 py-12" data-testid="tech-register-page">
      <div className="flex items-center gap-3">
        <span className="grid place-items-center h-10 w-10 rounded-lg bg-primary/10 text-primary">
          <Wrench className="h-5 w-5" />
        </span>
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-primary">Annuaire technicien</p>
          <h1 className="mt-0.5 font-display text-3xl font-black tracking-tight">
            {current ? "Ma fiche technicien" : "Devenir technicien référencé"}
          </h1>
        </div>
      </div>

      {current && StatusInfo && (
        <div className={`mt-6 inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold ${StatusInfo.class}`} data-testid="tech-status-badge">
          <StatusInfo.icon className="h-4 w-4" />
          {STATUS_LABELS[current.status].label}
          {current.status === "rejected" && current.reject_reason && (
            <span className="ml-2 font-normal">— {current.reject_reason}</span>
          )}
        </div>
      )}

      <Card className="mt-6 p-8 border-black/10">
        <form onSubmit={submit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="company_name">Raison sociale *</Label>
              <Input id="company_name" required value={form.company_name}
                onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                data-testid="tech-company" placeholder="SpaTech Provence" maxLength={200} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contact_name">Contact</Label>
              <Input id="contact_name" value={form.contact_name}
                onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                data-testid="tech-contact" placeholder="Roger Dupont" maxLength={120} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email public *</Label>
              <Input id="email" type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="tech-email" placeholder="contact@…" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="phone">Téléphone</Label>
              <Input id="phone" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="tech-phone" placeholder="+33 4 91 12 34 56" maxLength={30} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="address">Adresse</Label>
            <Input id="address" value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              data-testid="tech-address" placeholder="12 rue Paradis" maxLength={300} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="postal_code">Code postal *</Label>
              <Input id="postal_code" required value={form.postal_code}
                onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
                data-testid="tech-postal" placeholder="13001" inputMode="numeric" maxLength={10} />
            </div>
            <div className="md:col-span-2 space-y-1.5">
              <Label htmlFor="city">Ville *</Label>
              <Input id="city" required value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                data-testid="tech-city" placeholder="Marseille" maxLength={120} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Marques supportées</Label>
            <div className="flex flex-wrap gap-2">
              {brands.map((b) => {
                const active = form.brands_supported.includes(b.value);
                return (
                  <button
                    type="button"
                    key={b.value}
                    onClick={() => toggleBrand(b.value)}
                    data-testid={`tech-brand-${b.value}`}
                    className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                      active ? "border-primary bg-primary/10 text-primary" : "border-black/10 hover:border-primary/30"
                    }`}
                  >
                    {b.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="certifications">Certifications</Label>
            <Input id="certifications" value={form.certifications}
              onChange={(e) => setForm({ ...form, certifications: e.target.value })}
              data-testid="tech-certifs" placeholder="Certifié Balboa 2024, formation Gecko in.YE…" maxLength={500} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="description">Description ({form.description.length}/500)</Label>
            <Textarea id="description" value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value.slice(0, 500) })}
              data-testid="tech-description" placeholder="Décrivez votre société, votre expérience, vos zones d'intervention…"
              rows={4} maxLength={500} className="resize-none" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="website">Site web</Label>
              <Input id="website" value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
                data-testid="tech-website" placeholder="https://…" maxLength={300} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="radius">Rayon d'intervention (km)</Label>
              <Input id="radius" type="number" min={1} max={500} value={form.service_radius_km}
                onChange={(e) => setForm({ ...form, service_radius_km: Number(e.target.value) || 30 })}
                data-testid="tech-radius" />
            </div>
          </div>

          <div className="pt-4 border-t border-black/5 flex items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">
              Après enregistrement, votre fiche est vérifiée par notre équipe avant publication (24 à 48 h).
            </p>
            <Button type="submit" disabled={saving} className="rounded-full px-6" data-testid="tech-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : current ? "Mettre à jour" : "Créer ma fiche"}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
